/** A trip's phase as a small bar and a word — the schedule vocabulary (plan "Trips panel":
 *  past navy 35% fill, active solid navy, future white with a navy outline; an undated trip
 *  has no place on an axis, so a dashed muted outline). The word always carries the
 *  meaning; the bar only reinforces it (Icon-And-Words). */
import { Box, alpha, type Theme } from '@mui/material';

import type { TripPhase } from './tripPhase';

/** The phase in words, the same word the `phase` filter takes (lower-cased). */
export const TRIP_PHASE_LABEL: Record<TripPhase, string> = {
  past: 'Past',
  active: 'Active',
  future: 'Future',
  undated: 'Undated',
};

/** The phase's bar: 12×8 px, 2 px radius. */
export function tripPhaseMarkSx(phase: TripPhase): (t: Theme) => Record<string, unknown> {
  return (t) => {
    const navy = t.palette.primary.main;
    const base = {
      width: 12,
      height: 8,
      borderRadius: '2px',
      boxSizing: 'border-box',
      display: 'inline-block',
      flexShrink: 0,
    };
    switch (phase) {
      case 'past':
        return { ...base, border: `1px solid ${alpha(navy, 0.35)}`, bgcolor: alpha(navy, 0.35) };
      case 'active':
        return { ...base, bgcolor: navy };
      case 'future':
        return { ...base, border: `1.5px solid ${navy}`, bgcolor: t.palette.background.paper };
      default:
        return { ...base, border: `1.5px dashed ${t.palette.text.secondary}` };
    }
  };
}

export function TripPhaseLabel({ phase }: { phase: TripPhase }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box component="span" aria-hidden sx={tripPhaseMarkSx(phase)} />
      {TRIP_PHASE_LABEL[phase]}
    </Box>
  );
}
